import { useCallback } from 'react'
import toast from 'react-hot-toast'
import useIsReadOnly from '../../../hooks/useIsReadOnly'
import Log from '../../../services/api/log'
import { useMarkTaskDoneOrDeleted } from '../../../services/api/tasks.hooks'
import { shouldWaitForMotion } from '../../../styles/motion'
import { notifyReadOnlyRejection } from '../../../utils/readOnly'
import { useOptionalGoalCreation } from '../../goals/creation/shared/GoalCreationContext'
import { isGoalClosed } from '../../goals/goalTypes'
import GTCheckbox from '../GTCheckbox'

interface MarkTaskDoneButtonProps {
    isDone: boolean
    taskId: string
    isSelected: boolean
    sectionId?: string
    isDisabled?: boolean
    onMarkComplete?: () => void
    optimsticId?: string
}
const MarkTaskDoneButton = ({
    isDone,
    taskId,
    isSelected,
    sectionId,
    isDisabled,
    onMarkComplete,
    optimsticId,
}: MarkTaskDoneButtonProps) => {
    const { mutate: markTaskDoneOrDeleted } = useMarkTaskDoneOrDeleted()
    const goals = useOptionalGoalCreation()
    const isReadOnly = useIsReadOnly()
    const onMarkTaskDone = useCallback(() => {
        if (isDisabled) return
        // Stays clickable so a read-only click explains itself instead of doing nothing.
        if (isReadOnly) {
            notifyReadOnlyRejection()
            return false
        }
        if (onMarkComplete) onMarkComplete()
        markTaskDoneOrDeleted(
            {
                id: taskId,
                isDone: !isDone,
                waitForAnimation: shouldWaitForMotion(),
            },
            optimsticId
        )
        Log({
            taskId: taskId,
            sectionId: sectionId,
            isDone: !isDone,
        })
        // The server saves the contribution; this just says where the work went.
        const goalId = !isDone ? goals?.goalIdForTask(taskId, optimsticId) : undefined
        const goal = goalId ? goals?.allGoals.find((g) => g.id === goalId) : undefined
        // A closed goal (archived or achieved) takes no new credit, so it gets no toast either.
        if (goal && !isGoalClosed(goal)) toast.success(`Counted toward ${goal.title}`)
    }, [taskId, sectionId, isDone, onMarkComplete, goals, isReadOnly])

    return (
        <GTCheckbox
            isChecked={isDone}
            disabled={isDisabled}
            onChange={onMarkTaskDone}
            animated
            shortcutName="markAsDone"
            shortcutDisabled={!isSelected || isDisabled}
        />
    )
}

export default MarkTaskDoneButton
